import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ReserveHeader } from "@/components/ReserveHeader";
import { BackToDashboard } from "@/components/BackToDashboard";
import { Button } from "@/components/ui/button";
import { rankOpenShifts } from "@/lib/shift-matcher.functions";
import { registerInterest } from "@/lib/shifts.functions";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { ShiftCopilot } from "@/components/ShiftCopilot";

export const Route = createFileRoute("/_authenticated/bartender/matches")({
  component: MatchesPage,
  head: () => ({
    meta: [
      { title: "AI shift matches · The Hospitality Reserve" },
      { name: "description", content: "AI-ranked open shifts picked for you — with a one-line reason why each is a fit." },
      { name: "robots", content: "noindex" },
    ],
  }),
  errorComponent: () => <div className="p-10 text-center">Could not load matches.</div>,
  notFoundComponent: () => <div className="p-10 text-center">Not found.</div>,
});

function fmtTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function MatchesPage() {
  const rank = useServerFn(rankOpenShifts);
  const register = useServerFn(registerInterest);
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["ai-matches"],
    queryFn: () => rank(),
    staleTime: 60_000,
  });

  const onApply = async (shiftId: string) => {
    try {
      await register({ data: { shift_id: shiftId } });
      toast.success("Interest registered — the venue will review.");
      qc.invalidateQueries({ queryKey: ["ai-matches"] });
      nav({ to: "/bartender" });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not register");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <BackToDashboard />
        <header className="space-y-2">
          <p className="text-[0.6rem] tracking-wide-luxury uppercase text-muted-foreground flex items-center gap-2">
            <Sparkles className="w-3 h-3 text-accent" /> AI copilot
          </p>
          <h1 className="font-display text-3xl">Your best-fit shifts</h1>
          <p className="text-sm text-muted-foreground">
            Ranked by pay, distance, role match and your profile. Refreshed on demand.
          </p>
        </header>

        <ShiftCopilot />

        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? "Ranking…" : "Refresh matches"}
          </Button>
          {data && (
            <span className="text-[0.65rem] tracking-wide-luxury text-muted-foreground uppercase">
              {data.used_ai ? "AI-ranked" : "Sorted by pay"}
            </span>
          )}
        </div>

        {isLoading && <p className="text-sm text-muted-foreground">Ranking open shifts…</p>}
        {!isLoading && data && data.matches.length === 0 && (
          <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            No open shifts match right now. Check back soon or{" "}
            <Link to="/bartender/saved" className="underline">review your saved shifts</Link>.
          </div>
        )}
        <ul className="space-y-3">
          {data?.matches.map((m) => (
            <li key={m.shift_id} className="border border-border bg-card p-5 space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-display text-lg capitalize">{m.role_type}</p>
                  <p className="text-xs text-muted-foreground">{fmtTime(m.start_time)} → {fmtTime(m.end_time)}</p>
                  <p className="text-xs text-muted-foreground">
                    {m.postcode ?? "Location TBD"}
                    {m.distance_miles != null ? ` · ${m.distance_miles.toFixed(1)} mi away` : ""}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-display text-xl text-accent">£{(m.hourly_rate / 100).toFixed(2)}<span className="text-xs text-muted-foreground">/hr</span></p>
                  <p className="text-[0.6rem] tracking-wide-luxury text-muted-foreground uppercase mt-1">Score {m.score}</p>
                </div>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <Sparkles className="w-3.5 h-3.5 text-accent mt-0.5 shrink-0" />
                <p className="text-foreground/90">{m.why}</p>
              </div>
              <div className="flex justify-end">
                <Button size="sm" onClick={() => onApply(m.shift_id)}>Register interest</Button>
              </div>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}
